import React, { useState, useRef, useEffect } from 'react';
import { Dropdown, DropdownProps } from 'semantic-ui-react';
import { FarmersService } from '~/api/services/farmers/index';
import { useDebounce } from 'use-lodash-debounce';
import { useTranslation } from 'react-i18next';
import { Nullable } from '~/types';

const ROWS = 50;

interface Option {
  value: string;
  text: string;
  climateProgramRegistration: Nullable<boolean>
}

interface FarmersListProps extends Omit<DropdownProps, 'options' | 'onChange'> {
  value?: string | null;
  onChange?: (value: string | null, climateProgramRegistration, farmerName: string) => void;
}

const FarmersList: React.FC<FarmersListProps> = ({ value, onChange, ...rest }) => {
  const { t } = useTranslation();
  const pageRef = useRef(0);

  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (open) {
      loadOptions({ page: 0, reset: true });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!open) return;

    const el = document.getElementById('farmers-list-dropdown');
    if (!el) return;
    const menu = Array.from(el.children).find(
      child => (child as HTMLElement).classList.contains('menu')
    ) as HTMLDivElement | undefined;
    if (!menu) return;

    function handleScroll() {
      if (!hasMore || loading) return;
      const { scrollTop, scrollHeight, clientHeight } = menu!;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        loadOptions({ page: pageRef.current, reset: false });
      }
    }

    menu.addEventListener('scroll', handleScroll);
    return () => menu.removeEventListener('scroll', handleScroll);
  }, [open, hasMore, loading, debouncedSearch]);

  async function loadOptions({ page, reset }: { page: number; reset: boolean }) {
    if (loading || (!reset && !hasMore)) return;

    setLoading(true);
    try {
      const response = await FarmersService.getFarmers({
        page,
        rows: ROWS,
        search: searchQuery,
        isMain: null
      });

      const newOptions: Option[] = response.data.map(item => ({
        value: item.id,
        text: `${item.companyName} ${item.evsNumber ? `(${item.evsNumber})` : ''}`,
        climateProgramRegistration: item.climateProgramRegistration
      }));

      setOptions(prev => reset ? newOptions : [...prev, ...newOptions]);
      setHasMore(response.data.length === ROWS);
      pageRef.current = page + 1;
    } finally {
      setLoading(false);
    }
  }

  function handleOpen() {
    setOpen(true);
    loadOptions({ page: 0, reset: true });
  }

  function handleClose() {
    setOpen(false);
    setSearchQuery('');
    pageRef.current = 0;
    setHasMore(true);
  }

  function handleChange(_e: React.SyntheticEvent, data: DropdownProps) {
    const selected = options.find(o => o.value === data.value) || null;
    onChange?.(data.value as string | null, selected?.climateProgramRegistration, selected?.text);
  }

  function handleSearchChange(_e: React.SyntheticEvent, data: { searchQuery: string }) {
    setSearchQuery(data.searchQuery);
  }

  return (
    <Dropdown
      {...rest}
      id='farmers-list-dropdown'
      selection
      search={(opts) => opts}
      searchQuery={searchQuery}
      options={options}
      value={value ?? ''}
      loading={loading}
      open={open}
      placeholder={rest.placeholder ?? t('Select farmer')}
      noResultsMessage={loading ? t('Loading...') : t('No results found...')}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      selectOnBlur={false}
    />
  );
};

export default FarmersList;
